import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Clock, Phone, Calendar, Check } from "lucide-react";

const HERO = "/photos/showroom_inside/feature.jpg";
const SHOWROOM_PHOTOS = [
  "/photos/showroom_inside/s1.png",
  "/photos/showroom_inside/s3.png",
  "/photos/showroom_inside/s7.png",
  "/photos/showroom_inside/s10.jpg",
  "/photos/showroom_inside/s12.jpg",
  "/photos/showroom_inside/s_black_hood_herringbone.jpg",
];

const ADDRESS_LINE_1 = "168 Main Street";
const ADDRESS_LINE_2 = "Northborough, MA 01532";
const PHONE = "(781) 760-4524";
const PHONE_HREF = "tel:+17817604524";
const MAP_QUERY = encodeURIComponent("168 Main Street, Northborough, MA 01532");
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`;
const MAP_EMBED_URL = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`;

const HOURS = [
  { day: "Monday – Friday", hours: "By Appointment Only" },
  { day: "Saturday", hours: "By Appointment Only" },
  { day: "Sunday", hours: "By Appointment Only" },
];

const WHAT_YOULL_SEE = [
  { title: "Three premium cabinet lines", body: "Candlelight, Showplace, and Fabuwood — touch the doors, compare wood grains, see real finishes side by side." },
  { title: "Live slab samples", body: "Quartz, quartzite, marble and granite — fabricated in-house by our partners at Dream Stoneworks." },
  { title: "Fixtures, faucets & hardware", body: "Brushed brass, matte black, polished nickel — see them lit, not pixelated." },
  { title: "Tile, flooring & finishes", body: "Wide-plank oak, herringbone, large-format porcelain — bring your samples, we'll meet you halfway." },
];

const BRANDS = [
  { name: "Candlelight Cabinetry", logo: "/photos/cabinet_brands/candlelight.png" },
  { name: "Showplace Cabinetry", logo: "/photos/cabinet_brands/showplace.png" },
  { name: "Fabuwood Cabinetry", logo: "/photos/cabinet_brands/fabuwood.png" },
];

export default function ShowroomVisit() {
  // Today indicator for hours card
  const [todayIdx, setTodayIdx] = useState(-1);
  useEffect(() => {
    const d = new Date().getDay(); // 0 = Sun
    if (d >= 1 && d <= 5) setTodayIdx(0);
    else if (d === 6) setTodayIdx(1);
    else setTodayIdx(2);
  }, []);

  return (
    <div className="bg-white" data-testid="showroom-visit-page">
      {/* ============== HERO ============== */}
      <section className="relative bg-zinc-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img
            src={HERO}
            alt="Inside the Sleek Kitchen & Bath showroom"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/60 to-zinc-950" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-32 lg:pb-40">
          <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-300">Visit our showroom</div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] font-light leading-[0.95] mt-5 max-w-5xl">
            Touch the<br />
            <span className="italic text-zinc-300">finishes</span> before you<br />
            commit to them.
          </h1>
          <p className="mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed">
            Sleek Kitchen &amp; Bath — a full-line showroom on Main Street in Northborough.
            Walk through real kitchens, run your hand across cabinet doors, see slabs under
            our showroom lighting, and leave with a clear plan.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/booking?source=showroom-visit"
              className="inline-flex items-center gap-2 bg-white text-zinc-950 px-8 py-4 text-sm font-medium hover:bg-zinc-200 transition group"
              data-testid="visit-primary-cta"
            >
              <Calendar size={16} /> Book a showroom visit
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-4 py-4 border-b border-zinc-700 hover:border-white transition"
              data-testid="visit-directions-link"
            >
              <MapPin size={14} /> Get directions
            </a>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-4 py-4 border-b border-zinc-700 hover:border-white transition"
              data-testid="visit-call-link"
            >
              <Phone size={14} /> {PHONE}
            </a>
          </div>

          {/* Quick info strip floating at hero base */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 max-w-4xl">
            <div className="bg-zinc-950/70 backdrop-blur px-6 py-5">
              <div className="flex items-center gap-2 text-zinc-400 text-[10px] tracking-[0.3em] uppercase"><MapPin size={12} /> Address</div>
              <div className="mt-2 text-sm font-medium leading-snug">{ADDRESS_LINE_1}<br />{ADDRESS_LINE_2}</div>
            </div>
            <div className="bg-zinc-950/70 backdrop-blur px-6 py-5">
              <div className="flex items-center gap-2 text-zinc-400 text-[10px] tracking-[0.3em] uppercase"><Clock size={12} /> Hours</div>
              <div className="mt-2 text-sm font-medium leading-snug">
                {todayIdx >= 0 ? HOURS[todayIdx].hours : "—"}
                <div className="text-xs text-zinc-400 mt-0.5">{todayIdx >= 0 ? HOURS[todayIdx].day : ""}</div>
              </div>
            </div>
            <div className="bg-zinc-950/70 backdrop-blur px-6 py-5">
              <div className="flex items-center gap-2 text-zinc-400 text-[10px] tracking-[0.3em] uppercase"><Phone size={12} /> Call</div>
              <a href={PHONE_HREF} className="mt-2 text-sm font-medium leading-snug hover:text-zinc-300 block">{PHONE}</a>
            </div>
          </div>
        </div>
      </section>

      {/* ============== WHAT YOU'LL SEE ============== */}
      <section className="border-b border-zinc-200" data-testid="visit-what-youll-see">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14">
            <div className="md:col-span-4">
              <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">What you&apos;ll see</div>
              <h2 className="font-display text-4xl md:text-5xl font-light mt-5 leading-[1.05]">
                A working showroom — not a catalog.
              </h2>
              <p className="mt-6 text-zinc-600 leading-relaxed">
                Plan on 45 minutes. Bring a partner, a contractor, or just your Pinterest board.
                You&apos;ll leave knowing exactly what your kitchen or bath will look, sound, and feel like.
              </p>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-10">
              {WHAT_YOULL_SEE.map((item) => (
                <div key={item.title} className="border-l border-zinc-200 pl-6">
                  <Check size={18} className="text-zinc-900" />
                  <h3 className="font-display text-xl mt-3">{item.title}</h3>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== PHOTO STRIP ============== */}
      <section className="bg-zinc-50 border-b border-zinc-200" data-testid="visit-photo-strip">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">Inside</div>
          <h2 className="font-display text-3xl md:text-4xl font-light mt-4 max-w-2xl leading-tight">
            A few snapshots from the floor.
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {SHOWROOM_PHOTOS.map((src, i) => (
              <div key={src} className={`overflow-hidden ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/3]" : "aspect-[4/3]"} bg-zinc-200`}>
                <img
                  src={src}
                  alt="Sleek Kitchen & Bath showroom interior"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition duration-700"
                />
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/showroom"
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-zinc-950 border-b border-zinc-300 hover:border-zinc-950 pb-1 transition"
              data-testid="visit-explore-showroom"
            >
              Explore the full showroom <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== BRANDS STRIP ============== */}
      <section className="border-b border-zinc-200" data-testid="visit-brands">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">On the floor</div>
            <h2 className="font-display text-3xl md:text-4xl font-light mt-4 leading-tight">
              The brands you can see, touch, and spec — today.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 md:gap-12 items-center">
            {BRANDS.map((b) => (
              <div key={b.name} className="flex items-center justify-center h-16 md:h-20">
                <img src={b.logo} alt={b.name} loading="lazy" className="max-h-full max-w-[180px] object-contain opacity-80 hover:opacity-100 transition" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== HOURS + MAP ============== */}
      <section className="border-b border-zinc-200" data-testid="visit-hours-map">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-24 grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">Plan your visit</div>
            <h2 className="font-display text-3xl md:text-4xl font-light mt-4 leading-tight">
              Book a private hour with a designer.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed text-sm md:text-base">
              Visits are by appointment only. Booking ahead
              guarantees a designer is free to walk you through the floor and start a quote.
            </p>

            <div className="mt-10 divide-y divide-zinc-200 border-t border-b border-zinc-200">
              {HOURS.map((h, i) => (
                <div key={h.day} className={`flex justify-between py-4 ${i === todayIdx ? "bg-zinc-50 px-3 -mx-3" : ""}`}>
                  <div>
                    <span className="text-sm font-medium">{h.day}</span>
                    {i === todayIdx && <span className="ml-3 inline-block text-[10px] tracking-[0.2em] uppercase bg-zinc-900 text-white px-2 py-0.5">Today</span>}
                  </div>
                  <span className="text-sm text-zinc-600">{h.hours}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <MapPin size={16} className="mt-0.5 text-zinc-500 shrink-0" />
                <div>
                  {ADDRESS_LINE_1}<br />{ADDRESS_LINE_2}
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Phone size={16} className="mt-0.5 text-zinc-500 shrink-0" />
                <a href={PHONE_HREF} className="hover:underline">{PHONE}</a>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/booking?source=showroom-visit"
                className="inline-flex items-center gap-2 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition"
                data-testid="visit-secondary-cta"
              >
                Book a showroom visit <ArrowUpRight size={14} />
              </Link>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-950 border-b border-zinc-300 hover:border-zinc-950 pb-1 transition"
                data-testid="visit-directions-bottom"
              >
                Get directions <ArrowUpRight size={13} />
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="aspect-[4/3] md:aspect-[16/12] bg-zinc-100 border border-zinc-200">
              <iframe
                title="Sleek Kitchen & Bath showroom — map"
                src={MAP_EMBED_URL}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="bg-zinc-950 text-white" data-testid="visit-final-cta">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28 text-center">
          <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-400">Sleek Kitchen &amp; Bath</div>
          <h2 className="font-display text-4xl md:text-6xl font-light mt-5 leading-[1.05] max-w-3xl mx-auto">
            Come in. Bring questions.<br />Leave with a plan.
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/booking?source=showroom-visit-final"
              className="inline-flex items-center gap-2 bg-white text-zinc-950 px-8 py-4 text-sm font-medium hover:bg-zinc-200 transition"
              data-testid="visit-final-book"
            >
              Book a showroom visit <ArrowUpRight size={16} />
            </Link>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-4 py-4 border-b border-zinc-700 hover:border-white transition"
              data-testid="visit-final-call"
            >
              <Phone size={14} /> Or call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
